import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { DEFAULT_LEAD_FILTERS, EMAIL_STATUSES, toLeadQuery, type LeadFilters }
  from '@/features/leads/lead-filters';
import { buildUrl } from '@/lib/api/client';
import { useLeads } from '@/lib/api/queries';
import { cn } from '@/lib/cn';
import { formatInt } from '@/lib/format';

const STATUS_LABEL: Record<string, string> = {
  any: 'Tous', good: 'Email valide', risky: 'Email risqué', none: 'Sans email',
};

function Leads() {
  const [filters, setFilters] = useState<LeadFilters>(DEFAULT_LEAD_FILTERS);
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useLeads(filters);

  const rows = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const exportUrl = buildUrl('/leads/export.csv', {
    ...(toLeadQuery(filters) as Record<string, string | number | boolean>),
    limit: undefined,
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Leads</h1>
          <p className="text-sm text-secondary">
            {formatInt(rows.length)} ligne{rows.length > 1 ? 's' : ''} chargée
            {rows.length > 1 ? 's' : ''}
          </p>
        </div>
        <a
          href={exportUrl}
          className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-white"
        >
          Exporter en CSV
        </a>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-card bg-surface p-4 shadow-card">
        <input
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          placeholder="Rechercher un nom…"
          className="min-w-52 flex-1 rounded-control border border-hairline px-3 py-2 text-sm"
        />
        <div className="flex gap-1">
          {EMAIL_STATUSES.map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => setFilters((f) => ({ ...f, emailStatus: status }))}
              className={cn(
                'rounded-control px-3 py-1.5 text-xs font-semibold',
                filters.emailStatus === status
                  ? 'bg-tint-blue text-accent'
                  : 'text-secondary hover:bg-sunken',
              )}
            >
              {STATUS_LABEL[status]}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs text-secondary">
          <input
            type="checkbox"
            checked={filters.hasWebsite}
            onChange={(e) => setFilters((f) => ({ ...f, hasWebsite: e.target.checked }))}
            className="size-4"
          />
          Avec site web
        </label>
      </div>

      <div className="overflow-x-auto rounded-card bg-surface shadow-card">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="border-b border-hairline text-left text-xs uppercase tracking-wide text-secondary">
            <tr>
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3">Ville</th>
              <th className="px-4 py-3">Téléphone</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Site</th>
              <th className="px-4 py-3 text-right">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {rows.map((lead) => (
              <tr key={lead.id} className="hover:bg-sunken">
                <td className="max-w-72 truncate px-4 py-2.5 font-medium">{lead.name}</td>
                <td className="px-4 py-2.5 text-secondary">{lead.city ?? '—'}</td>
                <td className="px-4 py-2.5 tabular-nums text-secondary">
                  {lead.phone_e164 ?? '—'}
                </td>
                <td className="px-4 py-2.5">
                  {lead.email ? (
                    <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold',
                      lead.email_quality === 'good'
                        ? 'bg-tint-blue text-success'
                        : 'bg-tint-purple text-purple')}>
                      {lead.email}
                    </span>
                  ) : (
                    <span className="text-tertiary">—</span>
                  )}
                </td>
                <td className="max-w-48 truncate px-4 py-2.5 text-secondary">
                  {lead.website_domain ?? '—'}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums">
                  {lead.rating ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {isLoading ? <p className="p-6 text-center text-sm text-secondary">Chargement…</p> : null}
        {!isLoading && rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-secondary">
            Aucun lead ne correspond à ces filtres.
          </p>
        ) : null}
      </div>

      {hasNextPage ? (
        <button
          type="button"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className="mx-auto rounded-control border border-hairline bg-surface px-5 py-2 text-sm font-semibold"
        >
          {isFetchingNextPage ? 'Chargement…' : 'Charger la suite'}
        </button>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/leads')({ component: Leads });
